import { useCallback, useEffect, useRef, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import areResolvedPageBlocksEqual from "./areResolvedPageBlocksEqual";
import loadPageBlockRowContent from "./loadPageBlockRowContent";
import PageRowScheduler, {} from "./PageRowScheduler";
import useLoadBlockRowContent from "./useLoadBlockRowContent";
import useRecommendationIdentity from "./useRecommendationIdentity";
const pageBlockRowContentLogger = appLogger.withTag("PageBlockRowContent");
/** Rows start idle: only the scheduler moves a row to queued / loading. */
function buildInitialRowsState(blocks) {
    const map = new Map();
    for (const block of blocks) {
        map.set(block.key, { status: "idle", block });
    }
    return map;
}
export function mapBlocksToRows(blocks, rowStates) {
    return blocks.map((block) => rowStates.get(block.key) ?? { status: "idle", block });
}
function getNextRowStates(unstableBlocks, prevMap, hasContextChanged) {
    const nextMap = new Map();
    for (const block of unstableBlocks) {
        const existing = prevMap.get(block.key);
        if (!hasContextChanged && existing && areResolvedPageBlocksEqual(existing.block, block)) {
            nextMap.set(block.key, existing);
        }
        else {
            nextMap.set(block.key, { status: "idle", block });
        }
    }
    return nextMap;
}
/** Applies a scheduler transition without ever clobbering a committed result. */
export function applySchedulerStatus(prevMap, blockKey, status) {
    const existing = prevMap.get(blockKey);
    if (!existing) {
        return prevMap;
    }
    if (existing.status === "success" || existing.status === "empty") {
        return prevMap;
    }
    if (status === "idle" && existing.status === "error") {
        return prevMap;
    }
    const nextStatus = status === "active" ? "loading" : status;
    if (existing.status === nextStatus) {
        return prevMap;
    }
    const nextMap = new Map(prevMap);
    nextMap.set(blockKey, { status: nextStatus, block: existing.block });
    return nextMap;
}
function useStableBlocksAndStates(unstableBlocks, applicationId, pageKey, isNsfw) {
    const [stateStableBlocks, setStateStableBlocks] = useState(unstableBlocks);
    const [stateRowStates, setStateRowStates] = useState(() => buildInitialRowsState(unstableBlocks));
    const refPrevContext = useRef({ applicationId, pageKey, isNsfw });
    const isBlocksEqual = unstableBlocks.length === stateStableBlocks.length &&
        unstableBlocks.every((block, i) => areResolvedPageBlocksEqual(block, stateStableBlocks[i]));
    const hasContextChanged = refPrevContext.current.applicationId !== applicationId || refPrevContext.current.pageKey !== pageKey || refPrevContext.current.isNsfw !== isNsfw;
    if (!isBlocksEqual || hasContextChanged) {
        refPrevContext.current = { applicationId, pageKey, isNsfw };
        setStateStableBlocks(unstableBlocks);
        setStateRowStates((prevMap) => getNextRowStates(unstableBlocks, prevMap, hasContextChanged));
    }
    return [stateStableBlocks, stateRowStates, setStateRowStates];
}
function useHandleLoadResult(setStateRowStates, pageKey, refAutoRetryMap) {
    const refCurrentContext = useRef(null);
    const setCurrentContext = useCallback((context) => {
        refCurrentContext.current = context;
    }, []);
    // Clear on unmount so late responses hit the !current guard
    useEffect(() => {
        return () => {
            refCurrentContext.current = null;
        };
    }, []);
    const handler = useCallback((block, signal, retryRowFn, requestContext) => async ({ outcome, didConsumeAutoRetry }) => {
        const current = refCurrentContext.current;
        if (!current)
            return;
        const isContextStale = current.applicationId !== requestContext.applicationId ||
            current.pageKey !== requestContext.pageKey ||
            current.isNsfw !== requestContext.isNsfw;
        // Deliberate cancellation is never surfaced as a row error.
        if (signal.aborted || isContextStale || outcome.kind === "cancelled") {
            return;
        }
        if (didConsumeAutoRetry) {
            refAutoRetryMap.current.set(block.key, true);
        }
        setStateRowStates((prevMap) => {
            const nextMap = new Map(prevMap);
            if (outcome.kind === "error") {
                pageBlockRowContentLogger.error("Page block content request failed", {
                    pageKey,
                    blockKey: block.key,
                    category: outcome.category,
                    httpStatus: outcome.httpStatus,
                });
                nextMap.set(block.key, {
                    status: "error",
                    block,
                    retry: () => retryRowFn(block.key),
                });
                return nextMap;
            }
            nextMap.set(block.key, {
                status: outcome.kind === "success" ? "success" : "empty",
                block,
                ...(outcome.kind === "success" ? { items: outcome.items, total: outcome.total } : {}),
            });
            return nextMap;
        });
    }, [pageKey, setStateRowStates, refAutoRetryMap]);
    return [handler, setCurrentContext];
}
function useRetryRow(scheduler, refAutoRetryMap) {
    return useCallback((blockKey) => {
        refAutoRetryMap.current.set(blockKey, true);
        scheduler.retry(blockKey);
    }, [scheduler, refAutoRetryMap]);
}
function useManageBlockLevelChanges(scheduler, { blocks, applicationId, refEligibility, refAutoRetryMap, }) {
    const refPrevBlocks = useRef(blocks);
    useEffect(() => {
        const prevBlocks = refPrevBlocks.current;
        refPrevBlocks.current = blocks;
        if (prevBlocks === blocks)
            return;
        for (const oldBlock of prevBlocks) {
            const nextBlock = blocks.find((block) => block.key === oldBlock.key);
            if (nextBlock && areResolvedPageBlocksEqual(oldBlock, nextBlock))
                continue;
            scheduler.invalidate(oldBlock.key);
            if (!nextBlock) {
                refEligibility.current.delete(oldBlock.key);
                refAutoRetryMap.current.delete(oldBlock.key);
                continue;
            }
            const eligibility = refEligibility.current.get(oldBlock.key);
            if (eligibility && applicationId !== null) {
                scheduler.report(oldBlock.key, eligibility);
            }
        }
    }, [scheduler, blocks, applicationId, refAutoRetryMap, refEligibility]);
}
/**
 * Handles scheduler resets and re-seeds eligibility on request-context changes.
 */
function useSyncSchedulerOnContextChange(scheduler, { applicationId, refBlocks, refEligibility, pageKey, isNsfw, }) {
    useEffect(() => {
        scheduler.reset();
        if (applicationId === null) {
            return () => scheduler.reset();
        }
        const currentKeys = new Set(refBlocks.current.map((block) => block.key));
        const eligibilitiesToReport = [];
        for (const [blockKey, eligibility] of refEligibility.current) {
            if (currentKeys.has(blockKey)) {
                eligibilitiesToReport.push([blockKey, eligibility]);
            }
        }
        eligibilitiesToReport.forEach(([blockKey, eligibility]) => {
            scheduler.report(blockKey, eligibility);
        });
        return () => scheduler.reset();
    }, [scheduler, applicationId, pageKey, isNsfw, refBlocks, refEligibility]);
}
function resetRowsToIdle(prevMap) {
    const nextMap = new Map();
    for (const [blockKey, row] of prevMap) {
        nextMap.set(blockKey, { status: "idle", block: row.block });
    }
    return nextMap;
}
function requeueEligibleRows(scheduler, blocks, eligibilityByBlockKey, applicationId) {
    scheduler.reset();
    if (applicationId === null) {
        return;
    }
    for (const block of blocks) {
        const eligibility = eligibilityByBlockKey.get(block.key);
        if (eligibility) {
            scheduler.report(block.key, eligibility);
        }
    }
}
/**
 * Bridges the framework-agnostic PageRowScheduler to React state.
 * - The scheduler decides WHEN a row loads (queue, cap of 4, priority).
 * - This hook decides WHAT happens per row (load, commit, stale guard).
 * Platforms report viewport eligibility and can ask every eligible row to reload.
 */
function useRowScheduler({ blocks, applicationId, pageKey, isNsfw, recommendation, handleLoadResult, setStateRowStates, refAutoRetryMap, }) {
    const refBlocks = useRef(blocks);
    const refContext = useRef({ applicationId, pageKey, isNsfw });
    const refRecommendation = useRef(recommendation);
    const refRecommendationListeners = useRef(new Set());
    const refEligibility = useRef(new Map());
    refBlocks.current = blocks;
    refContext.current = { applicationId, pageKey, isNsfw };
    refRecommendation.current = recommendation;
    // Store up-to-date run handler and status change handler for the scheduler.
    const refRun = useRef(null);
    const refOnStatusChange = useRef(null);
    const [stateScheduler] = useState(() => new PageRowScheduler({
        run: (blockKey, signal) => refRun.current(blockKey, signal),
        onStatusChange: (blockKey, status) => {
            refOnStatusChange.current?.(blockKey, status);
        },
    }));
    refOnStatusChange.current = (blockKey, status) => {
        setStateRowStates((prevMap) => applySchedulerStatus(prevMap, blockKey, status));
    };
    useRecommendationIdentity({
        recommendationUserId: recommendation.userId,
        listeners: refRecommendationListeners,
        blocks: refBlocks,
        applicationId,
        eligibilityByBlockKey: refEligibility,
        scheduler: stateScheduler,
        setRowStates: setStateRowStates,
    });
    // --- split: retryRow as standalone ---
    const retryRow = useRetryRow(stateScheduler, refAutoRetryMap);
    // --- split: attach block loader to scheduler run handler ---
    refRun.current = useLoadBlockRowContent({
        refBlocks,
        refContext,
        refRecommendation,
        refRecommendationListeners,
        refAutoRetryMap,
        handleLoadResult,
        retryRow,
    });
    // Maintain scheduler order when blocks list changes.
    useEffect(() => {
        stateScheduler.setOrder(blocks.map((block) => block.key));
    }, [stateScheduler, blocks]);
    // Manage scheduler and eligibility on application context changes.
    useSyncSchedulerOnContextChange(stateScheduler, {
        applicationId,
        refBlocks,
        refEligibility,
        blocks,
        pageKey,
        isNsfw,
    });
    // Block-level changes, only invalidate what's changed/removed.
    useManageBlockLevelChanges(stateScheduler, {
        blocks,
        applicationId,
        refEligibility,
        refAutoRetryMap,
    });
    // Report/remember row eligibility.
    const reportRowEligibility = useCallback((blockKey, eligibility) => {
        refEligibility.current.set(blockKey, eligibility);
        if (refContext.current.applicationId === null)
            return;
        stateScheduler.report(blockKey, eligibility);
    }, [stateScheduler]);
    const refreshRows = useCallback(() => {
        refAutoRetryMap.current.clear();
        setStateRowStates((prevMap) => resetRowsToIdle(prevMap));
        requeueEligibleRows(stateScheduler, refBlocks.current, refEligibility.current, refContext.current.applicationId);
    }, [refAutoRetryMap, setStateRowStates, stateScheduler]);
    return { reportRowEligibility, refreshRows };
}
export default function usePageBlockRowsContent({ applicationId, pageKey, blocks: unstableBlocks, isNsfw, recommendation, }) {
    const [stateStableBlocks, stateRowStates, setStateRowStates] = useStableBlocksAndStates(unstableBlocks, applicationId, pageKey, isNsfw);
    const blocks = stateStableBlocks;
    const refAutoRetryMap = useRef(new Map());
    useEffect(() => {
        refAutoRetryMap.current.clear();
    }, [applicationId, pageKey, blocks, isNsfw]);
    const [handleLoadResult, setCurrentContext] = useHandleLoadResult(setStateRowStates, pageKey, refAutoRetryMap);
    useEffect(() => {
        setCurrentContext({ applicationId, pageKey, isNsfw });
    }, [applicationId, pageKey, isNsfw, setCurrentContext]);
    const { reportRowEligibility, refreshRows } = useRowScheduler({
        blocks,
        applicationId,
        pageKey,
        isNsfw,
        recommendation,
        handleLoadResult,
        setStateRowStates,
        refAutoRetryMap,
    });
    return {
        rows: mapBlocksToRows(blocks, stateRowStates),
        reportRowEligibility,
        refreshRows,
    };
}
